// Formulas for the finance calculators in FINANCE_PACK_SLUGS. Merged into
// financeMeta in browser-finance-tools.ts, so they run on the same
// BrowserFinanceCalculator component as every other finance tool.

import type { FinanceMeta } from '@/lib/browser-finance-tools';
import type { FinancePack1Slug } from '@/lib/finance-pack-slugs';

export function money(value: number): string {
  if (!Number.isFinite(value)) return '0';
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
export function plain(value: number, digits = 2): string {
  if (!Number.isFinite(value)) return '0';
  return value.toLocaleString('en-US', { maximumFractionDigits: digits });
}
export const pct = (value: number, digits = 2) => `${plain(value * 100, digits)}%`;

/** Level payment on an amortizing loan; rate is per period. */
export function payment(principal: number, rate: number, periods: number): number {
  if (periods <= 0) return 0;
  if (rate === 0) return principal / periods;
  return (principal * rate) / (1 - (1 + rate) ** -periods);
}

/** Monthly rate that makes `pmt` repay `principal` over `periods` (bisection). */
export function solveRate(principal: number, pmt: number, periods: number): number | null {
  if (principal <= 0 || pmt <= 0 || periods <= 0 || pmt * periods < principal) return null;
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 200; i += 1) {
    const mid = (lo + hi) / 2;
    if (payment(principal, mid, periods) > pmt) hi = mid;
    else lo = mid;
  }
  return (lo + hi) / 2;
}

/** Months to repay `balance` at monthly `rate` paying `pmt`; null if it never clears. */
export function payoff(balance: number, rate: number, pmt: number) {
  let months = 0;
  let interest = 0;
  let b = balance;
  while (b > 0.005 && months < 1200) {
    const i = b * rate;
    if (pmt <= i) return null;
    interest += i;
    b = b + i - pmt;
    months += 1;
  }
  return { months, interest };
}

export const yearsMonths = (months: number) => `${Math.floor(months / 12)} yr ${months % 12} mo`;

// US CPI-U annual averages (BLS series CUUR0000SA0 via FRED CPIAUCNS, 1982-84 = 100).
// 2025 averages the 11 months BLS published (October 2025 was never released after
// the government shutdown); 2026 averages January-August. Refresh from FRED yearly.
const CPI: Record<number, number> = {
  1913: 9.883,
  1914: 10.017,
  1915: 10.108,
  1916: 10.883,
  1917: 12.825,
  1918: 15.042,
  1919: 17.333,
  1920: 20.042,
  1921: 17.85,
  1922: 16.75,
  1923: 17.05,
  1924: 17.125,
  1925: 17.542,
  1926: 17.7,
  1927: 17.358,
  1928: 17.158,
  1929: 17.158,
  1930: 16.7,
  1931: 15.208,
  1932: 13.642,
  1933: 12.933,
  1934: 13.383,
  1935: 13.725,
  1936: 13.867,
  1937: 14.383,
  1938: 14.092,
  1939: 13.908,
  1940: 14.008,
  1941: 14.725,
  1942: 16.333,
  1943: 17.308,
  1944: 17.592,
  1945: 17.992,
  1946: 19.517,
  1947: 22.325,
  1948: 24.042,
  1949: 23.808,
  1950: 24.067,
  1951: 25.958,
  1952: 26.55,
  1953: 26.767,
  1954: 26.85,
  1955: 26.775,
  1956: 27.183,
  1957: 28.092,
  1958: 28.858,
  1959: 29.15,
  1960: 29.575,
  1961: 29.892,
  1962: 30.25,
  1963: 30.625,
  1964: 31.017,
  1965: 31.508,
  1966: 32.458,
  1967: 33.358,
  1968: 34.783,
  1969: 36.683,
  1970: 38.825,
  1971: 40.492,
  1972: 41.817,
  1973: 44.4,
  1974: 49.308,
  1975: 53.817,
  1976: 56.908,
  1977: 60.608,
  1978: 65.233,
  1979: 72.575,
  1980: 82.408,
  1981: 90.925,
  1982: 96.5,
  1983: 99.6,
  1984: 103.883,
  1985: 107.567,
  1986: 109.608,
  1987: 113.625,
  1988: 118.258,
  1989: 123.967,
  1990: 130.658,
  1991: 136.192,
  1992: 140.317,
  1993: 144.458,
  1994: 148.225,
  1995: 152.383,
  1996: 156.85,
  1997: 160.517,
  1998: 163.008,
  1999: 166.575,
  2000: 172.2,
  2001: 177.067,
  2002: 179.875,
  2003: 183.958,
  2004: 188.883,
  2005: 195.292,
  2006: 201.592,
  2007: 207.342,
  2008: 215.303,
  2009: 214.537,
  2010: 218.056,
  2011: 224.939,
  2012: 229.594,
  2013: 232.957,
  2014: 236.736,
  2015: 237.017,
  2016: 240.007,
  2017: 245.12,
  2018: 251.107,
  2019: 255.657,
  2020: 258.811,
  2021: 270.97,
  2022: 292.655,
  2023: 304.702,
  2024: 313.689,
  2025: 321.943,
  2026: 331.655,
};
const CPI_FIRST = 1913;
const CPI_LAST = 2026;

export const financePackMeta: Record<FinancePack1Slug, FinanceMeta> = {
  'emi-calculator': {
    key: 'emi-calculator',
    title: 'EMI Calculator',
    badge: 'EMI',
    fields: [
      { key: 'amount', label: 'Loan amount', placeholder: '1000000', default: '1000000' },
      { key: 'rate', label: 'Interest rate (% per year)', placeholder: '8.5', default: '8.5' },
      { key: 'tenure', label: 'Tenure', placeholder: '20', default: '20' },
      {
        key: 'unit',
        label: 'Tenure in',
        placeholder: '12',
        default: '12',
        kind: 'select',
        options: [
          { label: 'Years', value: '12' },
          { label: 'Months', value: '1' },
        ],
      },
    ],
    compute: ({ amount, rate, tenure, unit }) => {
      const n = Math.round(tenure * unit);
      const emi = payment(amount, rate / 1200, n);
      const total = emi * n;
      return [
        { label: 'Monthly EMI', value: money(emi) },
        { label: 'Total interest', value: money(total - amount) },
        { label: 'Total payment', value: money(total) },
        {
          label: 'Interest share of total',
          value: total ? pct((total - amount) / total, 1) : '0%',
        },
      ];
    },
  },
  'sip-calculator': {
    key: 'sip-calculator',
    title: 'SIP Calculator',
    badge: 'SIP',
    fields: [
      { key: 'monthly', label: 'Monthly investment', placeholder: '10000', default: '10000' },
      { key: 'rate', label: 'Expected return (% per year)', placeholder: '12', default: '12' },
      { key: 'years', label: 'Time period (years)', placeholder: '10', default: '10' },
      { key: 'stepup', label: 'Annual step-up (%)', placeholder: '0', default: '0' },
    ],
    compute: ({ monthly, rate, years, stepup }) => {
      // Installments at the start of each month, as AMCs and Indian SIP tools assume.
      const i = rate / 1200;
      const n = Math.round(years * 12);
      let value = 0;
      let invested = 0;
      let installment = monthly;
      for (let m = 0; m < n; m += 1) {
        if (m > 0 && m % 12 === 0) installment *= 1 + stepup / 100;
        value = (value + installment) * (1 + i);
        invested += installment;
      }
      return [
        { label: 'Total value', value: money(value) },
        { label: 'Invested amount', value: money(invested) },
        { label: 'Estimated returns', value: money(value - invested) },
      ];
    },
  },
  'swp-calculator': {
    key: 'swp-calculator',
    title: 'SWP Calculator',
    badge: 'SWP',
    fields: [
      { key: 'corpus', label: 'Total investment', placeholder: '1000000', default: '1000000' },
      {
        key: 'withdrawal',
        label: 'Withdrawal per month',
        placeholder: '10000',
        default: '10000',
      },
      { key: 'rate', label: 'Expected return (% per year)', placeholder: '8', default: '8' },
      { key: 'years', label: 'Time period (years)', placeholder: '5', default: '5' },
    ],
    compute: ({ corpus, withdrawal, rate, years }) => {
      const i = rate / 1200;
      const n = Math.round(years * 12);
      let balance = corpus;
      let withdrawn = 0;
      let lastedMonths = n;
      for (let m = 0; m < n; m += 1) {
        const take = Math.min(withdrawal, balance);
        balance = (balance - take) * (1 + i);
        withdrawn += take;
        if (balance <= 0.005) {
          lastedMonths = m + 1;
          balance = 0;
          break;
        }
      }
      return [
        { label: 'Final value', value: money(balance) },
        { label: 'Total withdrawn', value: money(withdrawn) },
        {
          label: 'Money lasts',
          value: balance > 0 ? `Full ${plain(years)} years` : yearsMonths(lastedMonths),
        },
      ];
    },
  },
  'fixed-deposit-calculator': {
    key: 'fixed-deposit-calculator',
    title: 'Fixed Deposit Calculator',
    badge: 'FD',
    fields: [
      { key: 'principal', label: 'Deposit amount', placeholder: '100000', default: '100000' },
      { key: 'rate', label: 'Interest rate (% per year)', placeholder: '7', default: '7' },
      { key: 'years', label: 'Time period (years)', placeholder: '5', default: '5' },
      {
        key: 'k',
        label: 'Compounding',
        placeholder: '4',
        default: '4',
        kind: 'select',
        options: [
          { label: 'Quarterly (most Indian banks)', value: '4' },
          { label: 'Monthly', value: '12' },
          { label: 'Half-yearly', value: '2' },
          { label: 'Yearly', value: '1' },
        ],
      },
    ],
    compute: ({ principal, rate, years, k }) => {
      const maturity = principal * (1 + rate / 100 / k) ** (k * years);
      return [
        { label: 'Maturity value', value: money(maturity) },
        { label: 'Interest earned', value: money(maturity - principal) },
        { label: 'Effective annual yield', value: pct((1 + rate / 100 / k) ** k - 1) },
      ];
    },
  },
  'rd-calculator': {
    key: 'rd-calculator',
    title: 'RD Calculator',
    badge: 'RD',
    fields: [
      { key: 'monthly', label: 'Monthly deposit', placeholder: '5000', default: '5000' },
      { key: 'rate', label: 'Interest rate (% per year)', placeholder: '7', default: '7' },
      { key: 'years', label: 'Time period (years)', placeholder: '5', default: '5' },
    ],
    compute: ({ monthly, rate, years }) => {
      // Indian banks compound RDs quarterly: each installment grows for its remaining
      // months at (1 + r/4)^(months/3).
      const n = Math.round(years * 12);
      let maturity = 0;
      for (let m = 0; m < n; m += 1) maturity += monthly * (1 + rate / 400) ** ((n - m) / 3);
      const deposited = monthly * n;
      return [
        { label: 'Maturity value', value: money(maturity) },
        { label: 'Total deposited', value: money(deposited) },
        { label: 'Interest earned', value: money(maturity - deposited) },
      ];
    },
  },
  'ppf-calculator': {
    key: 'ppf-calculator',
    title: 'PPF Calculator',
    badge: 'PPF',
    fields: [
      {
        key: 'yearly',
        label: 'Yearly investment (500 to 1,50,000)',
        placeholder: '150000',
        default: '150000',
      },
      { key: 'rate', label: 'Interest rate (% per year)', placeholder: '7.1', default: '7.1' },
      { key: 'years', label: 'Time period (15 years minimum)', placeholder: '15', default: '15' },
    ],
    compute: ({ yearly, rate, years }) => {
      // Deposited at the start of each year, compounded annually, as PPF credits it.
      let balance = 0;
      const n = Math.max(1, Math.round(years));
      for (let y = 0; y < n; y += 1) balance = (balance + yearly) * (1 + rate / 100);
      const invested = yearly * n;
      return [
        { label: 'Maturity value', value: money(balance) },
        { label: 'Total invested', value: money(invested) },
        { label: 'Total interest', value: money(balance - invested) },
      ];
    },
  },
  'nps-calculator': {
    key: 'nps-calculator',
    title: 'NPS Calculator',
    badge: 'NPS',
    fields: [
      { key: 'monthly', label: 'Monthly contribution', placeholder: '5000', default: '5000' },
      { key: 'age', label: 'Your age now', placeholder: '30', default: '30' },
      { key: 'rate', label: 'Expected return (% per year)', placeholder: '10', default: '10' },
      {
        key: 'annuityShare',
        label: 'Share used to buy an annuity (40% minimum)',
        placeholder: '40',
        default: '40',
      },
      { key: 'annuityRate', label: 'Annuity rate (% per year)', placeholder: '6', default: '6' },
    ],
    compute: ({ monthly, age, rate, annuityShare, annuityRate }) => {
      const n = Math.max(0, Math.round((60 - age) * 12));
      const i = rate / 1200;
      let corpus = 0;
      for (let m = 0; m < n; m += 1) corpus = (corpus + monthly) * (1 + i);
      const share = Math.min(100, Math.max(40, annuityShare)) / 100;
      const annuity = corpus * share;
      return [
        { label: 'Corpus at 60', value: money(corpus) },
        { label: 'Lump sum you can withdraw', value: money(corpus - annuity) },
        { label: 'Used to buy the annuity', value: money(annuity) },
        { label: 'Estimated monthly pension', value: money((annuity * annuityRate) / 1200) },
        { label: 'Total contributed', value: money(monthly * n) },
      ];
    },
  },
  'mutual-fund-calculator': {
    key: 'mutual-fund-calculator',
    title: 'Mutual Fund Calculator',
    badge: 'Mutual fund',
    fields: [
      {
        key: 'mode',
        label: 'Investment type',
        placeholder: '0',
        default: '0',
        kind: 'select',
        options: [
          { label: 'Lump sum (one-time)', value: '0' },
          { label: 'SIP (monthly)', value: '1' },
        ],
      },
      { key: 'amount', label: 'Amount', placeholder: '100000', default: '100000' },
      { key: 'rate', label: 'Expected return (% per year)', placeholder: '12', default: '12' },
      { key: 'years', label: 'Time period (years)', placeholder: '10', default: '10' },
    ],
    compute: ({ mode, amount, rate, years }) => {
      let value: number;
      let invested: number;
      if (mode === 1) {
        const i = rate / 1200;
        const n = Math.round(years * 12);
        value = 0;
        for (let m = 0; m < n; m += 1) value = (value + amount) * (1 + i);
        invested = amount * n;
      } else {
        value = amount * (1 + rate / 100) ** years;
        invested = amount;
      }
      return [
        { label: 'Total value', value: money(value) },
        { label: 'Invested amount', value: money(invested) },
        { label: 'Estimated returns', value: money(value - invested) },
      ];
    },
  },
  'salary-calculator': {
    key: 'salary-calculator',
    title: 'Salary Calculator',
    badge: 'Salary',
    fields: [
      { key: 'amount', label: 'Pay amount', placeholder: '60000', default: '60000' },
      {
        key: 'per',
        label: 'Per',
        placeholder: '1',
        default: '1',
        kind: 'select',
        options: [
          { label: 'Year', value: '1' },
          { label: 'Month', value: '12' },
          { label: 'Semi-month (twice a month)', value: '24' },
          { label: 'Two weeks', value: '26' },
          { label: 'Week', value: '52' },
          { label: 'Day', value: '-1' },
          { label: 'Hour', value: '-2' },
        ],
      },
      { key: 'hours', label: 'Hours per week', placeholder: '40', default: '40' },
      { key: 'days', label: 'Days per week', placeholder: '5', default: '5' },
    ],
    compute: ({ amount, per, hours, days }) => {
      const annual =
        per === -2 ? amount * hours * 52 : per === -1 ? amount * days * 52 : amount * per;
      return [
        { label: 'Annual', value: money(annual) },
        { label: 'Monthly', value: money(annual / 12) },
        { label: 'Semi-monthly', value: money(annual / 24) },
        { label: 'Biweekly', value: money(annual / 26) },
        { label: 'Weekly', value: money(annual / 52) },
        { label: 'Daily', value: money(days ? annual / (days * 52) : 0) },
        { label: 'Hourly', value: money(hours ? annual / (hours * 52) : 0) },
      ];
    },
  },
  'car-loan-calculator': {
    key: 'car-loan-calculator',
    title: 'Car Loan Calculator',
    badge: 'Car loan',
    fields: [
      { key: 'price', label: 'Car price', placeholder: '35000', default: '35000' },
      { key: 'down', label: 'Down payment', placeholder: '5000', default: '5000' },
      { key: 'trade', label: 'Trade-in value', placeholder: '0', default: '0' },
      { key: 'tax', label: 'Sales tax (%)', placeholder: '7', default: '7' },
      { key: 'rate', label: 'Interest rate (APR %)', placeholder: '7', default: '7' },
      { key: 'months', label: 'Loan term (months)', placeholder: '60', default: '60' },
    ],
    compute: ({ price, down, trade, tax, rate, months }) => {
      // Most US states tax the price after the trade-in credit.
      const taxAmount = Math.max(0, price - trade) * (tax / 100);
      const loan = Math.max(0, price + taxAmount - down - trade);
      const pmt = payment(loan, rate / 1200, months);
      const interest = pmt * months - loan;
      return [
        { label: 'Monthly payment', value: money(pmt) },
        { label: 'Amount financed', value: money(loan) },
        { label: 'Total interest', value: money(interest) },
        { label: 'Sales tax', value: money(taxAmount) },
        { label: 'Total cost (incl. down payment)', value: money(loan + interest + down + trade) },
      ];
    },
  },
  'inflation-calculator': {
    key: 'inflation-calculator',
    title: 'Inflation Calculator',
    badge: 'Inflation',
    fields: [
      { key: 'amount', label: 'Amount (US dollars)', placeholder: '100', default: '100' },
      { key: 'from', label: `From year (${CPI_FIRST}+)`, placeholder: '2000', default: '2000' },
      {
        key: 'to',
        label: `To year (up to ${CPI_LAST})`,
        placeholder: String(CPI_LAST),
        default: String(CPI_LAST),
      },
    ],
    compute: ({ amount, from, to }) => {
      const clamp = (y: number) => Math.min(CPI_LAST, Math.max(CPI_FIRST, Math.round(y)));
      const a = clamp(from);
      const b = clamp(to);
      const ratio = CPI[b] / CPI[a];
      const years = Math.abs(b - a);
      return [
        { label: `${plain(amount)} in ${a} is worth in ${b}`, value: money(amount * ratio) },
        { label: 'Cumulative inflation', value: pct(ratio - 1, 1) },
        {
          label: 'Average inflation per year',
          value: years ? pct(ratio ** (1 / (b - a)) - 1) : '0%',
        },
        { label: 'CPI-U (from → to)', value: `${plain(CPI[a], 3)} → ${plain(CPI[b], 3)}` },
      ];
    },
  },
  'cd-calculator': {
    key: 'cd-calculator',
    title: 'CD Calculator',
    badge: 'CD',
    fields: [
      { key: 'deposit', label: 'Deposit', placeholder: '10000', default: '10000' },
      { key: 'apy', label: 'APY (%)', placeholder: '4.5', default: '4.5' },
      { key: 'months', label: 'Term (months)', placeholder: '12', default: '12' },
    ],
    compute: ({ deposit, apy, months }) => {
      // APY already folds in compounding, so it applies directly per year.
      const balance = deposit * (1 + apy / 100) ** (months / 12);
      return [
        { label: 'Balance at maturity', value: money(balance) },
        { label: 'Interest earned', value: money(balance - deposit) },
      ];
    },
  },
  'roth-ira-calculator': {
    key: 'roth-ira-calculator',
    title: 'Roth IRA Calculator',
    badge: 'Roth IRA',
    fields: [
      { key: 'age', label: 'Current age', placeholder: '30', default: '30' },
      { key: 'retire', label: 'Retirement age', placeholder: '65', default: '65' },
      { key: 'balance', label: 'Current balance', placeholder: '10000', default: '10000' },
      { key: 'yearly', label: 'Annual contribution', placeholder: '7000', default: '7000' },
      { key: 'rate', label: 'Expected return (% per year)', placeholder: '7', default: '7' },
    ],
    compute: ({ age, retire, balance, yearly, rate }) => {
      const years = Math.max(0, Math.round(retire - age));
      let b = balance;
      for (let y = 0; y < years; y += 1) b = (b + yearly) * (1 + rate / 100);
      const contributed = balance + yearly * years;
      return [
        { label: `Balance at ${plain(retire, 0)} (tax-free)`, value: money(b) },
        { label: 'Total contributed', value: money(contributed) },
        { label: 'Tax-free growth', value: money(b - contributed) },
      ];
    },
  },
  'cagr-calculator': {
    key: 'cagr-calculator',
    title: 'CAGR Calculator',
    badge: 'CAGR',
    fields: [
      { key: 'start', label: 'Beginning value', placeholder: '10000', default: '10000' },
      { key: 'end', label: 'Ending value', placeholder: '20000', default: '20000' },
      { key: 'years', label: 'Number of years', placeholder: '5', default: '5' },
    ],
    compute: ({ start, end, years }) => {
      const valid = start > 0 && end > 0 && years > 0;
      return [
        { label: 'CAGR', value: valid ? pct((end / start) ** (1 / years) - 1) : '—' },
        { label: 'Total growth', value: start > 0 ? pct(end / start - 1, 1) : '—' },
      ];
    },
  },
  'interest-rate-calculator': {
    key: 'interest-rate-calculator',
    title: 'Interest Rate Calculator',
    badge: 'Rate',
    fields: [
      { key: 'amount', label: 'Loan amount', placeholder: '20000', default: '20000' },
      { key: 'pmt', label: 'Monthly payment', placeholder: '400', default: '400' },
      { key: 'months', label: 'Term (months)', placeholder: '60', default: '60' },
    ],
    compute: ({ amount, pmt, months }) => {
      const r = solveRate(amount, pmt, Math.round(months));
      if (r === null) return [{ label: 'Interest rate', value: 'Payments do not repay the loan' }];
      return [
        { label: 'Interest rate (APR)', value: pct(r * 12, 3) },
        { label: 'Total interest', value: money(pmt * months - amount) },
      ];
    },
  },
  'savings-calculator': {
    key: 'savings-calculator',
    title: 'Savings Calculator',
    badge: 'Savings',
    fields: [
      { key: 'initial', label: 'Initial deposit', placeholder: '5000', default: '5000' },
      { key: 'monthly', label: 'Monthly contribution', placeholder: '200', default: '200' },
      { key: 'rate', label: 'Interest rate (APY %)', placeholder: '4', default: '4' },
      { key: 'years', label: 'Years', placeholder: '10', default: '10' },
    ],
    compute: ({ initial, monthly, rate, years }) => {
      const i = (1 + rate / 100) ** (1 / 12) - 1;
      const n = Math.round(years * 12);
      let b = initial;
      for (let m = 0; m < n; m += 1) b = b * (1 + i) + monthly;
      const contributed = initial + monthly * n;
      return [
        { label: 'Final balance', value: money(b) },
        { label: 'Total contributions', value: money(contributed) },
        { label: 'Interest earned', value: money(b - contributed) },
      ];
    },
  },
  'rent-affordability-calculator': {
    key: 'rent-affordability-calculator',
    title: 'Rent Affordability Calculator',
    badge: 'Rent',
    fields: [
      { key: 'income', label: 'Gross annual income', placeholder: '60000', default: '60000' },
      { key: 'debts', label: 'Monthly debt payments', placeholder: '300', default: '300' },
    ],
    compute: ({ income, debts }) => {
      const monthly = income / 12;
      return [
        { label: '30% of income rule', value: money(monthly * 0.3) },
        { label: '40x rent landlord rule (max)', value: money(income / 40) },
        { label: 'After debts (36% total cap)', value: money(Math.max(0, monthly * 0.36 - debts)) },
      ];
    },
  },
  'pension-calculator': {
    key: 'pension-calculator',
    title: 'Pension Calculator',
    badge: 'Pension',
    fields: [
      { key: 'pot', label: 'Current pension pot', placeholder: '50000', default: '50000' },
      { key: 'you', label: 'Your monthly contribution', placeholder: '500', default: '500' },
      {
        key: 'employer',
        label: 'Employer monthly contribution',
        placeholder: '300',
        default: '300',
      },
      { key: 'rate', label: 'Growth (% per year)', placeholder: '5', default: '5' },
      { key: 'years', label: 'Years until retirement', placeholder: '25', default: '25' },
      { key: 'draw', label: 'Drawdown rate (% per year)', placeholder: '4', default: '4' },
    ],
    compute: ({ pot, you, employer, rate, years, draw }) => {
      const i = (1 + rate / 100) ** (1 / 12) - 1;
      const n = Math.round(years * 12);
      let b = pot;
      for (let m = 0; m < n; m += 1) b = b * (1 + i) + you + employer;
      const income = b * (draw / 100);
      return [
        { label: 'Pot at retirement', value: money(b) },
        { label: 'Annual income at drawdown rate', value: money(income) },
        { label: 'Monthly income', value: money(income / 12) },
        { label: 'Total contributed', value: money(pot + (you + employer) * n) },
      ];
    },
  },
  'mortgage-payoff-calculator': {
    key: 'mortgage-payoff-calculator',
    title: 'Mortgage Payoff Calculator',
    badge: 'Payoff',
    fields: [
      { key: 'balance', label: 'Mortgage balance', placeholder: '250000', default: '250000' },
      { key: 'rate', label: 'Interest rate (%)', placeholder: '6.5', default: '6.5' },
      { key: 'years', label: 'Years left on the loan', placeholder: '25', default: '25' },
      { key: 'extra', label: 'Extra payment per month', placeholder: '200', default: '200' },
    ],
    compute: ({ balance, rate, years, extra }) => {
      const r = rate / 1200;
      const n = Math.round(years * 12);
      const base = payment(balance, r, n);
      const plan = payoff(balance, r, base);
      const faster = payoff(balance, r, base + extra);
      if (!plan || !faster) return [{ label: 'Payoff', value: 'Check the inputs' }];
      return [
        { label: 'Regular monthly payment', value: money(base) },
        { label: 'Paid off in, with extra', value: yearsMonths(faster.months) },
        { label: 'Time saved', value: yearsMonths(plan.months - faster.months) },
        { label: 'Interest saved', value: money(plan.interest - faster.interest) },
      ];
    },
  },
  'home-equity-loan-calculator': {
    key: 'home-equity-loan-calculator',
    title: 'Home Equity Loan Calculator',
    badge: 'Home equity',
    fields: [
      { key: 'value', label: 'Home value', placeholder: '400000', default: '400000' },
      { key: 'balance', label: 'Mortgage balance', placeholder: '250000', default: '250000' },
      {
        key: 'cltv',
        label: 'Lender max loan-to-value (%)',
        placeholder: '85',
        default: '85',
      },
      { key: 'rate', label: 'Interest rate (%)', placeholder: '8.5', default: '8.5' },
      { key: 'years', label: 'Term (years)', placeholder: '15', default: '15' },
    ],
    compute: ({ value, balance, cltv, rate, years }) => {
      const max = Math.max(0, value * (cltv / 100) - balance);
      return [
        { label: 'Equity in the home', value: money(Math.max(0, value - balance)) },
        { label: 'Maximum you can borrow', value: money(max) },
        {
          label: 'Monthly payment on that amount',
          value: money(payment(max, rate / 1200, years * 12)),
        },
      ];
    },
  },
  'annuity-calculator': {
    key: 'annuity-calculator',
    title: 'Annuity Calculator',
    badge: 'Annuity',
    fields: [
      { key: 'principal', label: 'Starting balance', placeholder: '250000', default: '250000' },
      { key: 'rate', label: 'Interest rate (% per year)', placeholder: '5', default: '5' },
      { key: 'years', label: 'Payout period (years)', placeholder: '20', default: '20' },
      {
        key: 'k',
        label: 'Payout frequency',
        placeholder: '12',
        default: '12',
        kind: 'select',
        options: [
          { label: 'Monthly', value: '12' },
          { label: 'Quarterly', value: '4' },
          { label: 'Yearly', value: '1' },
        ],
      },
    ],
    compute: ({ principal, rate, years, k }) => {
      const per = payment(principal, rate / 100 / k, years * k);
      return [
        { label: 'Payout per period', value: money(per) },
        { label: 'Total paid out', value: money(per * years * k) },
        { label: 'Interest earned', value: money(per * years * k - principal) },
      ];
    },
  },
  'apy-calculator': {
    key: 'apy-calculator',
    title: 'APY Calculator',
    badge: 'APY',
    fields: [
      { key: 'apr', label: 'Interest rate (APR %)', placeholder: '5', default: '5' },
      {
        key: 'k',
        label: 'Compounding',
        placeholder: '12',
        default: '12',
        kind: 'select',
        options: [
          { label: 'Daily', value: '365' },
          { label: 'Monthly', value: '12' },
          { label: 'Quarterly', value: '4' },
          { label: 'Yearly', value: '1' },
        ],
      },
      {
        key: 'apy',
        label: 'Or convert an APY (%) back to APR',
        placeholder: '5.12',
        default: '5.12',
      },
    ],
    compute: ({ apr, k, apy }) => [
      { label: 'APY from that APR', value: pct((1 + apr / 100 / k) ** k - 1, 3) },
      { label: 'APR from that APY', value: pct(k * ((1 + apy / 100) ** (1 / k) - 1), 3) },
    ],
  },
  'salary-raise-calculator': {
    key: 'salary-raise-calculator',
    title: 'Salary Raise Calculator',
    badge: 'Raise',
    fields: [
      { key: 'salary', label: 'Current annual salary', placeholder: '60000', default: '60000' },
      { key: 'raise', label: 'Raise (%)', placeholder: '5', default: '5' },
    ],
    compute: ({ salary, raise }) => {
      const next = salary * (1 + raise / 100);
      return [
        { label: 'New annual salary', value: money(next) },
        { label: 'Raise per year', value: money(next - salary) },
        { label: 'Raise per month', value: money((next - salary) / 12) },
        { label: 'New hourly rate (2,080 h/yr)', value: money(next / 2080) },
      ];
    },
  },
  'apr-calculator': {
    key: 'apr-calculator',
    title: 'APR Calculator',
    badge: 'APR',
    fields: [
      { key: 'amount', label: 'Loan amount', placeholder: '20000', default: '20000' },
      { key: 'rate', label: 'Interest rate (%)', placeholder: '7', default: '7' },
      { key: 'months', label: 'Term (months)', placeholder: '60', default: '60' },
      { key: 'fees', label: 'Upfront fees', placeholder: '500', default: '500' },
    ],
    compute: ({ amount, rate, months, fees }) => {
      // Fees are paid out of the loan, so the borrower effectively receives less for
      // the same payments; APR is the rate that equates the two.
      const pmt = payment(amount, rate / 1200, months);
      const r = solveRate(amount - fees, pmt, Math.round(months));
      return [
        { label: 'Monthly payment', value: money(pmt) },
        { label: 'APR including fees', value: r === null ? '—' : pct(r * 12, 3) },
        { label: 'Total finance charge', value: money(pmt * months - amount + fees) },
      ];
    },
  },
  'boat-loan-calculator': {
    key: 'boat-loan-calculator',
    title: 'Boat Loan Calculator',
    badge: 'Boat loan',
    fields: [
      { key: 'price', label: 'Boat price', placeholder: '50000', default: '50000' },
      { key: 'down', label: 'Down payment', placeholder: '10000', default: '10000' },
      { key: 'rate', label: 'Interest rate (APR %)', placeholder: '8.5', default: '8.5' },
      { key: 'years', label: 'Term (years)', placeholder: '15', default: '15' },
    ],
    compute: ({ price, down, rate, years }) => {
      const loan = Math.max(0, price - down);
      const pmt = payment(loan, rate / 1200, years * 12);
      return [
        { label: 'Monthly payment', value: money(pmt) },
        { label: 'Loan amount', value: money(loan) },
        { label: 'Total interest', value: money(pmt * years * 12 - loan) },
      ];
    },
  },
  'budget-calculator': {
    key: 'budget-calculator',
    title: 'Budget Calculator',
    badge: 'Budget',
    fields: [
      { key: 'income', label: 'Monthly take-home income', placeholder: '5000', default: '5000' },
      { key: 'housing', label: 'Housing', placeholder: '1500', default: '1500' },
      { key: 'transport', label: 'Transport', placeholder: '400', default: '400' },
      { key: 'food', label: 'Food', placeholder: '600', default: '600' },
      { key: 'utilities', label: 'Utilities and phone', placeholder: '250', default: '250' },
      { key: 'debt', label: 'Debt payments', placeholder: '300', default: '300' },
      { key: 'other', label: 'Everything else', placeholder: '500', default: '500' },
      { key: 'saving', label: 'Savings', placeholder: '400', default: '400' },
    ],
    compute: ({ income, housing, transport, food, utilities, debt, other, saving }) => {
      const spent = housing + transport + food + utilities + debt + other + saving;
      return [
        { label: 'Total allocated', value: money(spent) },
        { label: 'Left over', value: money(income - spent) },
        { label: '50/30/20: needs', value: money(income * 0.5) },
        { label: '50/30/20: wants', value: money(income * 0.3) },
        { label: '50/30/20: savings and debt', value: money(income * 0.2) },
      ];
    },
  },
  'credit-card-interest-calculator': {
    key: 'credit-card-interest-calculator',
    title: 'Credit Card Interest Calculator',
    badge: 'Card interest',
    fields: [
      { key: 'balance', label: 'Card balance', placeholder: '5000', default: '5000' },
      { key: 'apr', label: 'APR (%)', placeholder: '22', default: '22' },
      { key: 'pmt', label: 'Monthly payment', placeholder: '200', default: '200' },
    ],
    compute: ({ balance, apr, pmt }) => {
      const r = apr / 1200;
      const plan = payoff(balance, r, pmt);
      return [
        { label: 'Interest this month', value: money(balance * r) },
        {
          label: 'Time to pay off',
          value: plan ? yearsMonths(plan.months) : 'Never: payment is below the interest',
        },
        { label: 'Total interest until paid off', value: plan ? money(plan.interest) : '—' },
      ];
    },
  },
  'net-worth-calculator': {
    key: 'net-worth-calculator',
    title: 'Net Worth Calculator',
    badge: 'Net worth',
    fields: [
      { key: 'cash', label: 'Cash and savings', placeholder: '10000', default: '10000' },
      {
        key: 'invest',
        label: 'Investments and retirement',
        placeholder: '30000',
        default: '30000',
      },
      { key: 'home', label: 'Home value', placeholder: '300000', default: '300000' },
      { key: 'otherA', label: 'Other assets (car, etc.)', placeholder: '15000', default: '15000' },
      { key: 'mortgage', label: 'Mortgage balance', placeholder: '220000', default: '220000' },
      { key: 'loans', label: 'Other loans', placeholder: '15000', default: '15000' },
      { key: 'cards', label: 'Credit card debt', placeholder: '3000', default: '3000' },
    ],
    compute: ({ cash, invest, home, otherA, mortgage, loans, cards }) => {
      const assets = cash + invest + home + otherA;
      const debts = mortgage + loans + cards;
      return [
        { label: 'Net worth', value: money(assets - debts) },
        { label: 'Total assets', value: money(assets) },
        { label: 'Total liabilities', value: money(debts) },
      ];
    },
  },
  'car-lease-calculator': {
    key: 'car-lease-calculator',
    title: 'Car Lease Calculator',
    badge: 'Lease',
    fields: [
      { key: 'msrp', label: 'MSRP (sticker price)', placeholder: '40000', default: '40000' },
      { key: 'price', label: 'Negotiated price', placeholder: '38000', default: '38000' },
      { key: 'down', label: 'Down payment', placeholder: '3000', default: '3000' },
      { key: 'residual', label: 'Residual value (% of MSRP)', placeholder: '58', default: '58' },
      { key: 'mf', label: 'Money factor', placeholder: '0.0025', default: '0.0025' },
      { key: 'months', label: 'Lease term (months)', placeholder: '36', default: '36' },
      { key: 'tax', label: 'Sales tax (%)', placeholder: '7', default: '7' },
    ],
    compute: ({ msrp, price, down, residual, mf, months, tax }) => {
      const cap = price - down;
      const res = msrp * (residual / 100);
      const depreciation = (cap - res) / months;
      const finance = (cap + res) * mf;
      const base = depreciation + finance;
      return [
        { label: 'Monthly payment with tax', value: money(base * (1 + tax / 100)) },
        { label: 'Before tax', value: money(base) },
        { label: 'Residual value', value: money(res) },
        { label: 'Equivalent APR', value: pct(mf * 24, 2) },
        { label: 'Total lease cost', value: money(base * (1 + tax / 100) * months + down) },
      ];
    },
  },
  'debt-to-income-ratio-calculator': {
    key: 'debt-to-income-ratio-calculator',
    title: 'Debt-to-Income Ratio Calculator',
    badge: 'DTI',
    fields: [
      { key: 'income', label: 'Gross monthly income', placeholder: '6000', default: '6000' },
      { key: 'housing', label: 'Rent or mortgage payment', placeholder: '1800', default: '1800' },
      { key: 'car', label: 'Car payment', placeholder: '400', default: '400' },
      { key: 'student', label: 'Student loans', placeholder: '300', default: '300' },
      { key: 'cards', label: 'Credit card minimums', placeholder: '150', default: '150' },
      { key: 'other', label: 'Other debt payments', placeholder: '0', default: '0' },
    ],
    compute: ({ income, housing, car, student, cards, other }) => {
      const debts = housing + car + student + cards + other;
      const dti = income ? debts / income : 0;
      const band =
        dti <= 0.36
          ? 'Healthy (36% or less)'
          : dti <= 0.43
            ? 'Manageable (37-43%)'
            : 'High (over 43%)';
      return [
        { label: 'Debt-to-income ratio', value: pct(dti, 1) },
        { label: 'Housing ratio (front-end)', value: pct(income ? housing / income : 0, 1) },
        { label: 'What lenders usually read', value: band },
      ];
    },
  },
  'glasgow-coma-scale-calculator': {
    key: 'glasgow-coma-scale-calculator',
    title: 'Glasgow Coma Scale Calculator',
    badge: 'GCS',
    fields: [
      {
        key: 'eye',
        label: 'Eye opening (E)',
        placeholder: '4',
        default: '4',
        kind: 'select',
        options: [
          { label: '4: Spontaneous', value: '4' },
          { label: '3: To sound', value: '3' },
          { label: '2: To pressure', value: '2' },
          { label: '1: None', value: '1' },
        ],
      },
      {
        key: 'verbal',
        label: 'Verbal response (V)',
        placeholder: '5',
        default: '5',
        kind: 'select',
        options: [
          { label: '5: Oriented', value: '5' },
          { label: '4: Confused', value: '4' },
          { label: '3: Words', value: '3' },
          { label: '2: Sounds', value: '2' },
          { label: '1: None', value: '1' },
        ],
      },
      {
        key: 'motor',
        label: 'Motor response (M)',
        placeholder: '6',
        default: '6',
        kind: 'select',
        options: [
          { label: '6: Obeys commands', value: '6' },
          { label: '5: Localising', value: '5' },
          { label: '4: Normal flexion', value: '4' },
          { label: '3: Abnormal flexion', value: '3' },
          { label: '2: Extension', value: '2' },
          { label: '1: None', value: '1' },
        ],
      },
    ],
    compute: ({ eye, verbal, motor }) => {
      const total = eye + verbal + motor;
      const band = total >= 13 ? 'Mild (13-15)' : total >= 9 ? 'Moderate (9-12)' : 'Severe (3-8)';
      return [
        { label: 'GCS total', value: `${total} / 15` },
        { label: 'Recorded as', value: `E${eye} V${verbal} M${motor}` },
        { label: 'Brain injury severity band', value: band },
      ];
    },
  },
  'bra-size-calculator': {
    key: 'bra-size-calculator',
    title: 'Bra Size Calculator',
    badge: 'Bra size',
    fields: [
      {
        key: 'under',
        label: 'Underbust (snug, around the ribcage)',
        placeholder: '32',
        default: '32',
      },
      { key: 'bust', label: 'Bust (fullest part, level)', placeholder: '36', default: '36' },
      {
        key: 'unit',
        label: 'Measured in',
        placeholder: '1',
        default: '1',
        kind: 'select',
        options: [
          { label: 'Inches', value: '1' },
          { label: 'Centimeters', value: '0.393701' },
        ],
      },
    ],
    compute: ({ under, bust, unit }) => {
      // Modern "no plus inches" method: band is the underbust rounded to the nearest
      // even inch; each inch of bust over the band is one cup.
      const u = under * unit;
      const b = bust * unit;
      let band = Math.round(u);
      if (band % 2) band += 1;
      const diff = Math.max(0, Math.round(b - band));
      const US = ['AA', 'A', 'B', 'C', 'D', 'DD', 'DDD', 'G', 'H', 'I', 'J', 'K'];
      const UK = ['AA', 'A', 'B', 'C', 'D', 'DD', 'E', 'F', 'FF', 'G', 'GG', 'H'];
      const i = Math.min(diff, US.length - 1);
      const sisterDown = i > 0 ? `${band + 2}${US[i - 1]}` : '—';
      const sisterUp = i < US.length - 1 ? `${band - 2}${US[i + 1]}` : '—';
      return [
        { label: 'US size', value: `${band}${US[i]}` },
        { label: 'UK size', value: `${band}${UK[i]}` },
        { label: 'Sister sizes (US)', value: `${sisterUp} / ${sisterDown}` },
      ];
    },
  },
  'protein-calculator': {
    key: 'protein-calculator',
    title: 'Protein Calculator',
    badge: 'Protein',
    fields: [
      { key: 'weight', label: 'Body weight', placeholder: '70', default: '70' },
      {
        key: 'unit',
        label: 'Weight in',
        placeholder: '1',
        default: '1',
        kind: 'select',
        options: [
          { label: 'Kilograms', value: '1' },
          { label: 'Pounds', value: '0.453592' },
        ],
      },
      {
        key: 'goal',
        label: 'Goal',
        placeholder: '1',
        default: '1',
        kind: 'select',
        options: [
          { label: 'Sedentary adult (RDA)', value: '0' },
          { label: 'Active / general fitness', value: '1' },
          { label: 'Build muscle', value: '2' },
          { label: 'Lose fat, keep muscle', value: '3' },
          { label: 'Older adult (65+)', value: '4' },
        ],
      },
    ],
    compute: ({ weight, unit, goal }) => {
      const kg = weight * unit;
      const RANGES: Array<[number, number]> = [
        [0.8, 0.8],
        [1.2, 1.6],
        [1.6, 2.2],
        [1.8, 2.4],
        [1.0, 1.2],
      ];
      const [lo, hi] = RANGES[goal] ?? RANGES[1];
      return [
        {
          label: 'Protein per day',
          value:
            lo === hi ? `${plain(kg * lo, 0)} g` : `${plain(kg * lo, 0)}-${plain(kg * hi, 0)} g`,
        },
        { label: 'Per kg of body weight', value: lo === hi ? `${lo} g` : `${lo}-${hi} g` },
        { label: 'Per meal (4 meals)', value: `${plain((kg * (lo + hi)) / 8, 0)} g` },
      ];
    },
  },
  'gfr-calculator': {
    key: 'gfr-calculator',
    title: 'eGFR Calculator',
    badge: 'eGFR',
    fields: [
      { key: 'scr', label: 'Serum creatinine', placeholder: '1.0', default: '1.0' },
      {
        key: 'unit',
        label: 'Creatinine unit',
        placeholder: '1',
        default: '1',
        kind: 'select',
        options: [
          { label: 'mg/dL', value: '1' },
          { label: 'µmol/L', value: '0.0113122' },
        ],
      },
      { key: 'age', label: 'Age (years)', placeholder: '50', default: '50' },
      {
        key: 'female',
        label: 'Sex',
        placeholder: '0',
        default: '0',
        kind: 'select',
        options: [
          { label: 'Male', value: '0' },
          { label: 'Female', value: '1' },
        ],
      },
    ],
    compute: ({ scr, unit, age, female }) => {
      // CKD-EPI 2021 creatinine equation (race-free), mL/min/1.73 m².
      const s = scr * unit;
      const k = female ? 0.7 : 0.9;
      const a = female ? -0.241 : -0.302;
      if (s <= 0 || age <= 0) return [{ label: 'eGFR', value: 'Enter creatinine and age' }];
      const egfr =
        142 *
        Math.min(s / k, 1) ** a *
        Math.max(s / k, 1) ** -1.2 *
        0.9938 ** age *
        (female ? 1.012 : 1);
      const stage =
        egfr >= 90
          ? 'G1: normal or high'
          : egfr >= 60
            ? 'G2: mildly decreased'
            : egfr >= 45
              ? 'G3a: mildly to moderately decreased'
              : egfr >= 30
                ? 'G3b: moderately to severely decreased'
                : egfr >= 15
                  ? 'G4: severely decreased'
                  : 'G5: kidney failure';
      return [
        { label: 'eGFR (mL/min/1.73 m²)', value: plain(egfr, 0) },
        { label: 'GFR category', value: stage },
      ];
    },
  },
  'bac-calculator': {
    key: 'bac-calculator',
    title: 'BAC Calculator',
    badge: 'BAC',
    fields: [
      {
        key: 'drinks',
        label: 'Standard drinks (14 g alcohol each)',
        placeholder: '3',
        default: '3',
      },
      { key: 'weight', label: 'Body weight', placeholder: '75', default: '75' },
      {
        key: 'unit',
        label: 'Weight in',
        placeholder: '1',
        default: '1',
        kind: 'select',
        options: [
          { label: 'Kilograms', value: '1' },
          { label: 'Pounds', value: '0.453592' },
        ],
      },
      {
        key: 'female',
        label: 'Sex',
        placeholder: '0',
        default: '0',
        kind: 'select',
        options: [
          { label: 'Male', value: '0' },
          { label: 'Female', value: '1' },
        ],
      },
      { key: 'hours', label: 'Hours since the first drink', placeholder: '2', default: '2' },
    ],
    compute: ({ drinks, weight, unit, female, hours }) => {
      // Widmark: BAC% = grams of alcohol / (body weight in grams × r) × 100 − 0.015 × hours.
      const grams = weight * unit * 1000;
      const r = female ? 0.55 : 0.68;
      const bac = grams > 0 ? Math.max(0, ((drinks * 14) / (grams * r)) * 100 - 0.015 * hours) : 0;
      return [
        { label: 'Estimated BAC', value: `${bac.toFixed(3)}%` },
        { label: 'Hours until about 0.00%', value: plain(bac / 0.015, 1) },
        {
          label: 'Over the US 0.08% driving limit?',
          value: bac >= 0.08 ? 'Yes' : 'No, but any alcohol impairs driving',
        },
      ];
    },
  },
  'body-shape-calculator': {
    key: 'body-shape-calculator',
    title: 'Body Shape Calculator',
    badge: 'Body shape',
    fields: [
      { key: 'bust', label: 'Bust', placeholder: '36', default: '36' },
      { key: 'waist', label: 'Waist (narrowest point)', placeholder: '28', default: '28' },
      { key: 'hip', label: 'Hips (widest point)', placeholder: '38', default: '38' },
    ],
    compute: ({ bust, waist, hip }) => {
      // Any unit works: the shape comes from ratios, so inches and cm give the same answer.
      if (bust <= 0 || waist <= 0 || hip <= 0)
        return [{ label: 'Shape', value: 'Enter all three' }];
      let shape: string;
      if (waist >= 0.9 * hip && waist >= 0.9 * bust) shape = 'Apple (round)';
      else if (Math.abs(bust - hip) / hip <= 0.05 && waist <= 0.75 * Math.min(bust, hip))
        shape = 'Hourglass';
      else if (hip - bust > 0.05 * hip) shape = 'Pear (triangle)';
      else if (bust - hip > 0.05 * hip) shape = 'Inverted triangle';
      else shape = 'Rectangle';
      return [
        { label: 'Body shape', value: shape },
        { label: 'Waist-to-hip ratio', value: plain(waist / hip, 2) },
        { label: 'Waist-to-bust ratio', value: plain(waist / bust, 2) },
      ];
    },
  },
  'weight-loss-calculator': {
    key: 'weight-loss-calculator',
    title: 'Weight Loss Calculator',
    badge: 'Weight loss',
    fields: [
      { key: 'current', label: 'Current weight', placeholder: '90', default: '90' },
      { key: 'goal', label: 'Goal weight', placeholder: '80', default: '80' },
      {
        key: 'perUnit',
        label: 'Weight in',
        placeholder: '7700',
        default: '7700',
        kind: 'select',
        options: [
          { label: 'Kilograms', value: '7700' },
          { label: 'Pounds', value: '3500' },
        ],
      },
      { key: 'deficit', label: 'Daily calorie deficit', placeholder: '500', default: '500' },
    ],
    compute: ({ current, goal, perUnit, deficit }) => {
      // Rule of thumb: ~7,700 kcal per kg (3,500 per lb) of body fat. Real loss slows
      // as weight drops, so treat this as the fastest realistic pace.
      const toLose = Math.max(0, current - goal);
      if (deficit <= 0) return [{ label: 'Time to goal', value: 'Enter a deficit above zero' }];
      const days = (toLose * perUnit) / deficit;
      const unit = perUnit === 3500 ? 'lb' : 'kg';
      return [
        { label: 'Weight to lose', value: `${plain(toLose, 1)} ${unit}` },
        {
          label: 'Time to goal',
          value: `${plain(days / 7, 1)} weeks (${plain(days / 30.44, 1)} months)`,
        },
        { label: 'Loss per week', value: `${plain((deficit * 7) / perUnit, 2)} ${unit}` },
      ];
    },
  },
  'height-calculator': {
    key: 'height-calculator',
    title: 'Height Calculator',
    badge: 'Height',
    fields: [
      { key: 'father', label: "Father's height", placeholder: '178', default: '178' },
      { key: 'mother', label: "Mother's height", placeholder: '165', default: '165' },
      {
        key: 'cm',
        label: 'Heights in',
        placeholder: '1',
        default: '1',
        kind: 'select',
        options: [
          { label: 'Centimeters', value: '1' },
          { label: 'Inches', value: '2.54' },
        ],
      },
      {
        key: 'boy',
        label: "Child's sex",
        placeholder: '1',
        default: '1',
        kind: 'select',
        options: [
          { label: 'Boy', value: '1' },
          { label: 'Girl', value: '0' },
        ],
      },
    ],
    compute: ({ father, mother, cm, boy }) => {
      // Mid-parental height: average the parents, then +/- 6.5 cm for sex.
      // About 95% of children land within 8.5 cm of it.
      const mid = (father * cm + mother * cm + (boy ? 13 : -13)) / 2;
      const feet = (c: number) => {
        const inches = c / 2.54;
        return `${Math.floor(inches / 12)}' ${plain(inches % 12, 1)}"`;
      };
      return [
        { label: 'Predicted adult height', value: `${plain(mid, 1)} cm (${feet(mid)})` },
        { label: 'Likely range (95%)', value: `${plain(mid - 8.5, 1)}-${plain(mid + 8.5, 1)} cm` },
      ];
    },
  },
  'rmd-calculator': {
    key: 'rmd-calculator',
    title: 'RMD Calculator',
    badge: 'RMD',
    fields: [
      { key: 'age', label: 'Age this year', placeholder: '75', default: '75' },
      {
        key: 'balance',
        label: 'Account balance on Dec 31 last year',
        placeholder: '500000',
        default: '500000',
      },
    ],
    compute: ({ age, balance }) => {
      // IRS Uniform Lifetime Table (2022 onward), ages 72-120.
      const T = [
        27.4, 26.5, 25.5, 24.6, 23.7, 22.9, 22.0, 21.1, 20.2, 19.4, 18.5, 17.7, 16.8, 16.0, 15.2,
        14.4, 13.7, 12.9, 12.2, 11.5, 10.8, 10.1, 9.5, 8.9, 8.4, 7.8, 7.3, 6.8, 6.4, 6.0, 5.6, 5.2,
        4.9, 4.6, 4.3, 4.1, 3.9, 3.7, 3.5, 3.4, 3.3, 3.1, 3.0, 2.9, 2.8, 2.7, 2.5, 2.3, 2.0,
      ];
      const a = Math.round(age);
      if (a < 73)
        return [{ label: 'Required minimum distribution', value: 'None yet: RMDs start at 73' }];
      const divisor = T[Math.min(a, 120) - 72];
      return [
        { label: 'Required minimum distribution', value: money(balance / divisor) },
        { label: 'IRS life expectancy factor', value: plain(divisor, 1) },
        { label: 'Share of the balance', value: pct(1 / divisor, 2) },
      ];
    },
  },
  'maintenance-calorie-calculator': {
    key: 'maintenance-calorie-calculator',
    title: 'Maintenance Calorie Calculator',
    badge: 'Maintenance',
    fields: [
      { key: 'weight', label: 'Weight (kg)', placeholder: '70', default: '70' },
      { key: 'height', label: 'Height (cm)', placeholder: '175', default: '175' },
      { key: 'age', label: 'Age', placeholder: '30', default: '30' },
      {
        key: 'male',
        label: 'Sex',
        placeholder: '1',
        default: '1',
        kind: 'select',
        options: [
          { label: 'Male', value: '1' },
          { label: 'Female', value: '0' },
        ],
      },
      {
        key: 'activity',
        label: 'Activity level',
        placeholder: '1.55',
        default: '1.55',
        kind: 'select',
        options: [
          { label: 'Sedentary (desk job)', value: '1.2' },
          { label: 'Light (1-3 workouts/week)', value: '1.375' },
          { label: 'Moderate (3-5 workouts/week)', value: '1.55' },
          { label: 'Very active (6-7/week)', value: '1.725' },
          { label: 'Athlete (twice a day)', value: '1.9' },
        ],
      },
    ],
    compute: ({ weight, height, age, male, activity }) => {
      // Mifflin-St Jeor resting rate times an activity multiplier.
      const bmr = 10 * weight + 6.25 * height - 5 * age + (male ? 5 : -161);
      const keep = bmr * activity;
      return [
        { label: 'Maintenance calories', value: `${plain(keep, 0)} kcal/day` },
        { label: 'Resting (BMR)', value: `${plain(bmr, 0)} kcal/day` },
        { label: 'To lose ~0.5 kg/week', value: `${plain(keep - 500, 0)} kcal/day` },
        { label: 'To gain ~0.25 kg/week', value: `${plain(keep + 250, 0)} kcal/day` },
      ];
    },
  },
};

// The health and body calculators in this pack show no money, so they get their own footnote.
for (const slug of [
  'glasgow-coma-scale-calculator',
  'bra-size-calculator',
  'protein-calculator',
  'gfr-calculator',
  'bac-calculator',
  'body-shape-calculator',
  'weight-loss-calculator',
  'height-calculator',
  'maintenance-calorie-calculator',
] as const) {
  financePackMeta[slug].note =
    'Calculations run locally in your browser; nothing is uploaded. An estimate, not medical advice.';
}
